import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';
import SearchResults from '../components/search/SearchResults';
import SearchFilters from '../components/search/SearchFilters';
import DimensionSearch from '../components/search/DimensionSearch';

const { FiSearch, FiFilter, FiX, FiSliders } = FiIcons;

const Search = () => {
  const { products, loading, getManufacturers, getCategories } = useDatabase();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [searchMode, setSearchMode] = useState('text'); // 'text' oder 'dimensions'
  
  const manufacturers = getManufacturers();
  const categories = getCategories();

  const performSearch = (searchQuery = query, searchFilters = filters) => {
    let searchResults = [...products];

    // Text-basierte Suche
    if (searchQuery) {
      const searchTerm = searchQuery.toLowerCase();
      searchResults = searchResults.filter(product =>
        product.manufacturer_sku.toLowerCase().includes(searchTerm) ||
        product.normalized_sku.toLowerCase().includes(searchTerm) ||
        product.manufacturer.toLowerCase().includes(searchTerm) ||
        JSON.stringify(product.specifications).toLowerCase().includes(searchTerm)
      );
    }

    // Filter anwenden
    if (searchFilters.category) {
      searchResults = searchResults.filter(p => p.etim_class_code === searchFilters.category);
    }

    if (searchFilters.manufacturer) {
      searchResults = searchResults.filter(p => p.manufacturer === searchFilters.manufacturer);
    }

    if (searchFilters.dimensions) {
      Object.entries(searchFilters.dimensions).forEach(([key, range]) => {
        searchResults = searchResults.filter(p => {
          const value = p.specifications[key];
          return value && value >= range.min && value <= range.max;
        });
      });
    }

    if (searchFilters.inStock) {
      searchResults = searchResults.filter(p => p.availability === 'In Stock');
    }

    if (searchFilters.priceRange) {
      const { min, max } = searchFilters.priceRange;
      searchResults = searchResults.filter(p => {
        const price = p.price_eur;
        return price && (!min || price >= min) && (!max || price <= max);
      });
    }

    setResults(searchResults);
  };

  useEffect(() => {
    performSearch();
  }, [query, filters, products]);

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleDimensionSearch = (dimensionFilters) => {
    setFilters(prev => ({ ...prev, ...dimensionFilters }));
  };

  const clearFilters = () => {
    setFilters({});
    setQuery('');
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Erweiterte Suche</h1>
            <p className="text-gray-600 mt-2">
              Finden Sie präzise die benötigten Industriekomponenten
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters 
                  ? 'bg-black text-white' 
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiFilter} className="w-5 h-5" />
              <span>Filter</span>
            </button>
          </div>
        </div>

        {/* Search Mode Toggle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => setSearchMode('text')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMode === 'text'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Textsuche
            </button>
            <button
              onClick={() => setSearchMode('dimensions')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchMode === 'dimensions'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiSliders} className="w-4 h-4 mr-2" />
              Maßsuche
            </button>
          </div>

          {searchMode === 'text' ? (
            <div className="relative">
              <SafeIcon 
                icon={FiSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" 
              />
              <input
                type="text"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Suche nach Artikelnummer, Hersteller oder Spezifikation..."
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
              />
            </div>
          ) : (
            <DimensionSearch onSearch={handleDimensionSearch} />
          )}

          {/* Active Filters */}
          {Object.keys(filters).length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Aktive Filter:</span>
              {Object.entries(filters).map(([key, value]) => (
                <span
                  key={key}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-800 border border-red-100"
                >
                  {key}: {typeof value === 'object' ? JSON.stringify(value) : value}
                  <button
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters[key];
                      setFilters(newFilters);
                    }}
                    className="ml-2 hover:text-red-900"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-800 font-medium underline"
              >
                Alle löschen
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        {showFilters && (
          <SearchFilters
            manufacturers={manufacturers}
            categories={categories}
            onFilterChange={handleFilterChange}
            currentFilters={filters}
          />
        )}

        {/* Search Results */}
        <SearchResults
          results={results}
          loading={loading}
          query={query}
          totalResults={results.length}
        />
      </motion.div>
    </div>
  );
};

export default Search;